import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { clampToZero } from 'shared/lib/number/number';
import {
    formatReportCurrency,
    formatReportPercent,
} from 'shared/lib/reportFormatters';
import { ReportMetric } from 'shared/ui/ReportMetric';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import type { SavingsInsightsSummaryDto } from 'entities/ReportSavingsSummary';

interface SavingsSummaryTotalSavingsMetricProps {
    summary: SavingsInsightsSummaryDto | null;
    primaryAmount: string | number | null;
    showPercentage?: boolean;
    loading?: boolean;
}

export const SavingsSummaryTotalSavingsMetric = memo(
    ({
        summary,
        primaryAmount,
        showPercentage = true,
        loading = false,
    }: SavingsSummaryTotalSavingsMetricProps) => {
        const { t } = useTranslation();

        const savingsPercentValue = summary
            ? clampToZero(summary.savingsPercentage)
            : null;

        const savingsPercentLabel =
            savingsPercentValue == null
                ? ''
                : formatReportPercent(savingsPercentValue);

        const isZeroSaved = savingsPercentValue === 0;

        const detailRowSx = {
            color: 'common.black',
            fontSize: '13px',
            lineHeight: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '10px',
        } as const;

        const detailValueSx = {
            color: 'common.black',
            fontVariantNumeric: 'tabular-nums' as const,
            fontWeight: 600,
        } as const;
        const totalSpendAll = summary?.totalSpendAll;
        const hasTotalSpendAll =
            totalSpendAll !== undefined &&
            totalSpendAll !== null &&
            totalSpendAll !== '';

        return (
            <ReportMetric
                title={t('savingsSummary.totalSavings')}
                primary={
                    primaryAmount == null
                        ? '-'
                        : formatReportCurrency(clampToZero(primaryAmount))
                }
                primaryColor="metrics.positive"
                primaryTooltip={t('savingsSummary.totalSavingsTooltip')}
                secondary={
                    showPercentage && savingsPercentLabel
                        ? `${savingsPercentLabel} ${t(
                              'savingsSummary.savedSuffix',
                          )}`
                        : undefined
                }
                secondarySx={{
                    color: isZeroSaved
                        ? 'rgba(0, 0, 0, 0.50)'
                        : 'metrics.positive',
                    fontSize: '14px',
                    fontWeight: 700,
                }}
                loading={loading}
                minHeight={showPercentage ? '215px' : '200px'}
                sx={{
                    minWidth: '240px',
                }}
            >
                <Box sx={detailRowSx}>
                    <span>{t('savingsSummary.totalSpend')}</span>
                    <Box component="span" sx={detailValueSx}>
                        {hasTotalSpendAll
                            ? formatReportCurrency(totalSpendAll)
                            : '-'}
                    </Box>
                </Box>
                <Box sx={detailRowSx}>
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'flex-start',
                            gap: '5px',
                        }}
                    >
                        <span>{t('savingsSummary.competitiveSpend')}</span>
                        <DynamicTooltip
                            content={t(
                                'savingsSummary.competitiveSpendTooltip',
                            )}
                            containerStyle={{
                                height: '16px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    fontSize: 18,
                                    color: '#B7B7B7',
                                }}
                                data-pdf-tooltip="true"
                            />
                        </DynamicTooltip>
                    </Box>
                    <Box component="span" sx={detailValueSx}>
                        {summary
                            ? formatReportCurrency(summary.totalSpend)
                            : '-'}
                    </Box>
                </Box>
            </ReportMetric>
        );
    },
);

SavingsSummaryTotalSavingsMetric.displayName =
    'SavingsSummaryTotalSavingsMetric';
